import React from 'react';
import { observer } from 'mobx-react';
import { Box, Typography, Container } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import LanguageIcon from '@mui/icons-material/Language';
import PublisherCard from '../../components/publisherCard';
import PublisherCardSkeleton from '../../components/publisherCardSkeleton';
import usePublisher from '../../../store/models/publisher/usePublisher';
import { useEnvStore } from '../../../store/models/env';
import { formatTimezoneLabel } from '../../../commons/utils/formatTimezone';
import * as styles from './styles';

const SKELETON_COUNT = 3; // matches the grid's widest breakpoint (lg: 3 columns)

function NearMe({ onSync, onShare }) {
    const {
        nearMePublishers,
        whatsHotPublisherIds,
        nearMePublishersLoading,
    } = usePublisher();
    const env = useEnvStore();
    // env.countryInfo resolves during initApp(), before usePublisherInit()'s
    // fetches even start (see getCountryInfo.js) — always set by the time
    // this section has real content to show, so no observed token needed.
    const timezoneLabel = formatTimezoneLabel(env.countryInfo?.timezone);

    function renderSectionHeader() {
        return (
            <Box sx={styles.sectionHeader}>
                <Box>
                    <Box sx={styles.eyebrow}>
                        <PlaceIcon sx={styles.pinIcon} />
                        Near you
                        {timezoneLabel && (
                            <>
                                <Box component="span" sx={styles.eyebrowDivider}>·</Box>
                                <LanguageIcon sx={styles.pinIcon} />
                                {timezoneLabel}
                            </>
                        )}
                    </Box>
                    <Typography variant="h2" sx={styles.title}>
                        What's happening in{' '}
                        <Box component="span" sx={styles.gradientText}>your area</Box>
                    </Typography>
                    <Typography sx={styles.subtitle}>
                        Other local publishers worth exploring.
                    </Typography>
                </Box>
            </Box>
        );
    }

    function renderGrid() {
        return (
            <Box sx={styles.grid}>
                {nearMePublishers.map((publisher) => (
                    <PublisherCard
                        key={publisher.pubId}
                        publisher={publisher}
                        hotRank={whatsHotPublisherIds[String(publisher.pubId)]}
                        onSync={onSync}
                        onShare={onShare}
                    />
                ))}
            </Box>
        );
    }

    function renderSkeletonGrid() {
        return (
            <Box sx={styles.grid}>
                {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                    <PublisherCardSkeleton key={i} />
                ))}
            </Box>
        );
    }

    if (!nearMePublishersLoading && !nearMePublishers.length) return null;

    return (
        <Box component="section" sx={styles.section}>
            <Container maxWidth="xl">
                {renderSectionHeader()}
                {nearMePublishersLoading ? renderSkeletonGrid() : renderGrid()}
            </Container>
        </Box>
    );
}

export default observer(NearMe);
